/**
 * Wishlist Page Component
 */

import { useState, useEffect } from 'react';
import { useWishlist, useShop } from '@/context';
import { productsApi } from '@/api/client';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { AddToCart } from '../AddToCart';
import type { Product } from '@/types';
import styles from './WishlistPage.module.css';

export function WishlistPage() {
  const { items: wishlistIds, removeItem, isLoading: wishlistLoading } = useWishlist();
  const { t, formatPrice, urls } = useShop();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, [wishlistIds]);

  const loadProducts = async () => {
    if (wishlistIds.length === 0) {
      setProducts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Fetch products one by one (could be optimized with a batch endpoint)
      const loaded = await Promise.all(
        wishlistIds.map((id) =>
          productsApi.getProduct(id).catch(() => null)
        )
      );
      setProducts(loaded.filter((p): p is Product => p !== null));
    } catch {
      // Ignore errors
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async (productId: number) => {
    try {
      await removeItem(productId);
    } catch {
      // Error handled in context
    }
  };

  if (isLoading || wishlistLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className={styles.empty}>
        <HeartIcon />
        <p>Votre liste de favoris est vide</p>
        <Button
          variant="primary"
          onClick={() => (window.location.href = urls.shop)}
        >
          Découvrir nos produits
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('myWishlist')}</h2>

      <div className={styles.grid}>
        {products.map((product) => (
          <div key={product.id} className={styles.card}>
            <button
              className={styles.removeButton}
              onClick={() => handleRemove(product.id)}
              aria-label={t('removeFromWishlist')}
            >
              <CloseIcon />
            </button>

            <a href={product.permalink} className={styles.imageLink}>
              <img
                src={product.featured_image?.url || '/placeholder.jpg'}
                alt={product.name}
                className={styles.image}
              />
            </a>

            <div className={styles.content}>
              <a href={product.permalink} className={styles.name}>
                {product.name}
              </a>

              <div className={styles.price}>
                {product.on_sale && product.sale_price ? (
                  <>
                    <span className={styles.salePrice}>
                      {formatPrice(product.sale_price)}
                    </span>
                    <span className={styles.regularPrice}>
                      {formatPrice(product.regular_price)}
                    </span>
                  </>
                ) : (
                  <span>{formatPrice(product.price)}</span>
                )}
              </div>

              <div className={styles.actions}>
                <AddToCart
                  productId={product.id}
                  stock={product.stock}
                  showQuantity={false}
                  size="small"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeartIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={styles.emptyIcon}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
